import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { FormProvider, useForm } from "react-hook-form";
import { PaziaTimeInput } from "components/atoms/Inputs";
import { COURSE_SCHEDULE_TYPE_OPTIONS } from "consts/courseScheduleType";
import { useTopicGroupsOptions, useTrainersOptions, useCourseGroupsOptions } from "api/hooks";
import { TopicApi } from "api";
import {
  PaziaFormButton,
  PaziaSelect,
  PaziaInput,
  PaziaLoader,
  PaziaMultiSelect,
} from "components/atoms";
import { minutesToTime } from "utils";
import { PaziaFromQuill } from "components/atoms/PaziaQuill/PaziaFromQuill";

interface TopicFormProps {
  onSubmit: (data: TopicFormValues) => Promise<void>;
  topic?: TopicApi;
}
export const TopicForm = ({ onSubmit, topic }: TopicFormProps) => {
  const { isLoading: loadingTrainers, options: trainersOptions } = useTrainersOptions();
  const { loading: loadingGroups, options: groupsOptions } = useTopicGroupsOptions();
  const { loading: loadingCourseGroups, options: courseGroupsOptions } = useCourseGroupsOptions();
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      [TOPIC_FORM_NAME.TITLE]: topic?.title,
      [TOPIC_FORM_NAME.DESCRIPTION]: topic?.description,
      [TOPIC_FORM_NAME.TIME]: minutesToTime(topic?.time?.toString()),
      [TOPIC_FORM_NAME.TYPE]: topic?.type,
      [TOPIC_FORM_NAME.COURSE_GROUP_IDS]: topic?.courseGroups.map((x) => x.courseGroupId + ""),
      [TOPIC_FORM_NAME.GROUP_IDS]: topic?.topicGroups.map((x) => x.id + ""),
      [TOPIC_FORM_NAME.TRAINERS_IDS]: topic?.trainers.map((x) => x.trainerId + ""),
    },
  });

  const { handleSubmit, reset } = methods;

  if (loadingTrainers || loadingGroups || loadingCourseGroups) return <PaziaLoader position="center" />;

  const onSubmitHandler = async (data: TopicFormValues) => {
    await onSubmit(data);
    reset();
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitHandler)} className="grid-cols-2 gap-8 md:grid">
        <div className="flex flex-col gap-2 md:gap-4">
          <PaziaInput name={TOPIC_FORM_NAME.TITLE} label="Tytuł" type="text" required />
          <div>
            <PaziaFromQuill label="Opis" name={TOPIC_FORM_NAME.DESCRIPTION} />
          </div>
        </div>
        <div className="mt-2 flex flex-col gap-2 md:my-0 md:gap-4">
          <PaziaSelect
            name={TOPIC_FORM_NAME.TYPE}
            options={COURSE_SCHEDULE_TYPE_OPTIONS}
            label="Typ"
            required
            isClearable={false}
          />
          <PaziaMultiSelect name={TOPIC_FORM_NAME.GROUP_IDS} options={groupsOptions} label="Grupa tematów" />
          <PaziaMultiSelect name={TOPIC_FORM_NAME.COURSE_GROUP_IDS} options={courseGroupsOptions} label="Grupa kursu" />
          <PaziaTimeInput name={TOPIC_FORM_NAME.TIME} label="Spodziewany czas trwania [min.]" />

          <PaziaMultiSelect name={TOPIC_FORM_NAME.TRAINERS_IDS} options={trainersOptions} label="Trenerzy" />
        </div>
        <div className="col-start-2 mt-4 flex justify-end md:my-0">
          <PaziaFormButton title="Zapisz" type="success" />
        </div>
      </form>
    </FormProvider>
  );
};
// TODO tooltip do TimeInputa
// TODO Uzupelnic grupe

const TOPIC_FORM_NAME = {
  TITLE: "title",
  DESCRIPTION: "description",
  TIME: "time",
  TYPE: "type",
  GROUP_IDS: "groupIds",
  COURSE_GROUP_IDS: "courseGroupIds",
  ADD_TRAINER: "addTrainer",
  TRAINERS_IDS: "trainersIds",
} as const;
const schema = yup
  .object({
    [TOPIC_FORM_NAME.TITLE]: yup.string().required(),
    [TOPIC_FORM_NAME.DESCRIPTION]: yup.string(),
    [TOPIC_FORM_NAME.TIME]: yup.string(),
    [TOPIC_FORM_NAME.TYPE]: yup.number().required(),
    [TOPIC_FORM_NAME.GROUP_IDS]: yup.array().of(yup.string()),
    [TOPIC_FORM_NAME.TRAINERS_IDS]: yup.array().of(yup.string()),
    [TOPIC_FORM_NAME.ADD_TRAINER]: yup.string(),
    [TOPIC_FORM_NAME.COURSE_GROUP_IDS]: yup.array().of(yup.string()),
  })
  .required();
export type TopicFormValues = yup.InferType<typeof schema>;
